import React, { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../../styles/movements/movements.css";
import { formatDateTime, formatDuration } from "../../utils/locationAnalytics";

const LIGHT_TILE_URL =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const LIGHT_TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

const makeDivIcon = (className, label = "") =>
  L.divIcon({
    className: "movements-div-icon",
    html: `<div class="movements-marker ${className}">${label}</div>`,
    iconSize: className === "stop" ? [28, 28] : [22, 22],
    iconAnchor: className === "stop" ? [14, 14] : [11, 11],
  });

const escapeHtml = (value) => {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const buildPopupHtml = ({ title, place, meta }) =>
  `<div class="movements-popup-title">${escapeHtml(title)}</div>` +
  (place
    ? `<div class="movements-popup-place">${escapeHtml(place)}</div>`
    : "") +
  (meta ? `<div class="movements-popup-meta">${meta}</div>` : "");

/**
 * Forensic movement map.
 *
 * Props:
 *   - points:        sorted array of { latitude, longitude, recorded_at, ... }
 *   - segments:      array of point arrays (splitIntoSegments output)
 *   - stops:         array from detectStops
 *   - playheadIndex: integer index into `points` or null
 *   - onSelectStop:  (stop) => void
 */
export const MovementMap = ({
  points = [],
  segments = [],
  stops = [],
  playheadIndex = null,
  onSelectStop,
  getLabel,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const pathLayerRef = useRef(null);
  const markersLayerRef = useRef(null);
  const playheadMarkerRef = useRef(null);

  // One-time map init.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
      worldCopyJump: true,
      // Stockholm fallback center; will be replaced as soon as we have points.
      center: [59.3293, 18.0686],
      zoom: 12,
    });

    L.tileLayer(LIGHT_TILE_URL, {
      attribution: LIGHT_TILE_ATTRIBUTION,
      subdomains: "abcd",
      maxZoom: 19,
      detectRetina: true,
    }).addTo(map);

    pathLayerRef.current = L.layerGroup().addTo(map);
    markersLayerRef.current = L.layerGroup().addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      pathLayerRef.current = null;
      markersLayerRef.current = null;
      playheadMarkerRef.current = null;
    };
  }, []);

  // Invalidate size whenever the window resizes so the map fills its shell.
  useEffect(() => {
    const onResize = () => mapRef.current?.invalidateSize();
    window.addEventListener("resize", onResize);
    // Kick once after mount so the initial layout settles.
    const id = setTimeout(onResize, 50);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(id);
    };
  }, []);

  // Redraw path + markers whenever point data changes.
  useEffect(() => {
    const map = mapRef.current;
    const pathLayer = pathLayerRef.current;
    const markersLayer = markersLayerRef.current;
    if (!map || !pathLayer || !markersLayer) return;

    pathLayer.clearLayers();
    markersLayer.clearLayers();
    playheadMarkerRef.current = null;

    if (points.length === 0) return;

    // Draw each segment as a soft halo + sharp core line for the glow effect.
    segments.forEach((segment) => {
      if (segment.length < 2) return;
      const latlngs = segment.map((p) => [p.latitude, p.longitude]);
      L.polyline(latlngs, {
        className: "movements-path-halo",
        interactive: false,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(pathLayer);
      L.polyline(latlngs, {
        className: "movements-path-core",
        interactive: false,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(pathLayer);
    });

    // Start + end markers.
    const first = points[0];
    const last = points[points.length - 1];

    const resolveLabel = (lat, lng) => {
      if (typeof getLabel !== "function") return null;
      const value = getLabel(lat, lng);
      return typeof value === "string" && value.length > 0 ? value : null;
    };

    L.marker([first.latitude, first.longitude], {
      icon: makeDivIcon("start", "A"),
      keyboard: false,
    })
      .bindPopup(
        buildPopupHtml({
          title: "Startpunkt",
          place: resolveLabel(first.latitude, first.longitude),
          meta: formatDateTime(first.recorded_at),
        })
      )
      .addTo(markersLayer);

    L.marker([last.latitude, last.longitude], {
      icon: makeDivIcon("end", "B"),
      keyboard: false,
    })
      .bindPopup(
        buildPopupHtml({
          title: "Senaste punkt",
          place: resolveLabel(last.latitude, last.longitude),
          meta: formatDateTime(last.recorded_at),
        })
      )
      .addTo(markersLayer);

    // Stop markers numbered by chronological order.
    stops.forEach((stop, idx) => {
      const marker = L.marker([stop.center.latitude, stop.center.longitude], {
        icon: makeDivIcon("stop", String(idx + 1)),
      })
        .bindPopup(
          buildPopupHtml({
            title: `Stopp ${idx + 1}`,
            place: resolveLabel(stop.center.latitude, stop.center.longitude),
            meta: `${formatDuration(stop.dwellMs)} &middot; ${formatDateTime(
              new Date(stop.startAt).toISOString()
            )}`,
          })
        )
        .on("click", () => onSelectStop?.(stop))
        .addTo(markersLayer);
      // Prevent the click from bubbling to the map.
      marker.on("click", (e) => L.DomEvent.stopPropagation(e));
    });

    // Fit to bounds with some padding.
    const bounds = L.latLngBounds(points.map((p) => [p.latitude, p.longitude]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
    }
  }, [points, segments, stops, onSelectStop, getLabel]);

  // Update the playhead marker when the scrubber moves.
  useEffect(() => {
    const markersLayer = markersLayerRef.current;
    if (!markersLayer) return;

    if (
      playheadIndex == null ||
      playheadIndex < 0 ||
      playheadIndex >= points.length
    ) {
      if (playheadMarkerRef.current) {
        markersLayer.removeLayer(playheadMarkerRef.current);
        playheadMarkerRef.current = null;
      }
      return;
    }

    const p = points[playheadIndex];
    if (!playheadMarkerRef.current) {
      playheadMarkerRef.current = L.marker([p.latitude, p.longitude], {
        icon: makeDivIcon("playhead"),
        keyboard: false,
        interactive: false,
        zIndexOffset: 1000,
      }).addTo(markersLayer);
    } else {
      playheadMarkerRef.current.setLatLng([p.latitude, p.longitude]);
    }
  }, [playheadIndex, points]);

  const isEmpty = points.length === 0;

  const overlay = useMemo(
    () => (
      <div className="movements-map-overlay">
        <div className="movements-map-legend" aria-label="Kartsymboler">
          <h4>Teckenförklaring</h4>
          <div className="movements-legend-row">
            <span className="movements-legend-swatch" />
            <span>Rörelseväg</span>
          </div>
          <div className="movements-legend-row">
            <span className="movements-legend-dot start" />
            <span>Första position</span>
          </div>
          <div className="movements-legend-row">
            <span className="movements-legend-dot end" />
            <span>Senaste position</span>
          </div>
          <div className="movements-legend-row">
            <span className="movements-legend-dot stop" />
            <span>Stopp (dröjt kvar)</span>
          </div>
        </div>
      </div>
    ),
    []
  );

  return (
    <div className="movements-map-shell">
      <div className="movements-map" ref={containerRef} />
      {overlay}
      {isEmpty && (
        <div className="movements-map-empty" role="status">
          <h3>Inga positioner i valt tidsspann</h3>
          <p>
            Användaren har antingen inte aktiverat kontinuerlig platsdelning,
            eller så finns det inga rapporterade positioner mellan dessa
            tidpunkter. Justera tidsspannet för att söka i en annan period.
          </p>
        </div>
      )}
    </div>
  );
};

export default MovementMap;
